import { ApiManyResponse, ApiOneResponse, many, one } from '@actiris/forms';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Language } from '@core/models/language';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UpdateCredentialForm } from 'src/app/shared/components/update-credential-form/update-credential-form';
import { ApplicationResponseDTO } from '../../models/api/security/application-dto';
import { MailType } from '../../models/api/security/mail-type';
import { Application } from '../../models/application';
import { CompteStatuts } from '../../models/compte-statuts';

@Injectable({
    providedIn: 'root',
})
export class SecurityApiService {
    private http = inject(HttpClient);

    public validateKey(key: string, type: MailType): Observable<boolean> {
        const request: { cle: string; type: string } = {
            cle: key,
            type: type,
        };
        return this.http.post<boolean>(`api/Cle/validate`, request).pipe(one());
    }

    public activateAccount(key: string): Observable<boolean> {
        const request: { cle: string; type: string } = {
            cle: key,
            type: MailType.Activation,
        };
        return this.http.post<boolean>(`/api/Compte/activate`, request);
    }

    public resetPassword(key: string, data: UpdateCredentialForm): Observable<{ statusPrecedent: CompteStatuts }> {
        const request: { cle: string; password: string; passwordConfirmation: string } = {
            cle: key,
            password: data.password,
            passwordConfirmation: data.confirm,
        };
        return this.http.post<{ statusPrecedent: CompteStatuts }>(`/api/Compte/password/reset`, request).pipe(one());
    }

    public unlockAccount(key: string, form: UpdateCredentialForm): Observable<void> {
        const request: { cle: string; password: string; passwordConfirmation: string } = {
            cle: key,
            password: form.password,
            passwordConfirmation: form.confirm,
        };
        return this.http.post<void>(`/api/Compte/unlock`, request);
    }

    public createAccount(key: string, form: UpdateCredentialForm): Observable<CompteStatuts> {
        const request: { cle: string; password: string; passwordConfirmation: string } = {
            cle: key,
            password: form.password,
            passwordConfirmation: form.confirm,
        };
        return this.http.post<{ statut: CompteStatuts }>(`/api/Compte/cree`, request).pipe(map((result) => result.statut));
    }

    /** Retourne la liste des applications accessibles pour l'utilisateur connecté */
    public getAccessibleApplications(): Observable<Application[]> {
        return this.http.get<ApiManyResponse<ApplicationResponseDTO>>(`/api/application`).pipe(
            many(),
            map((apps) => {
                return (apps || []).map((item) => {
                    return {
                        description: {
                            fr: item.descriptionFR,
                            nl: item.descriptionNL,
                        },
                        icon: { color: item.iconColor, name: item.iconName },
                        name: { fr: item.nameFR, nl: item.nameNL },
                        code: item.code,
                        urls: {
                            fr: item.urlHomeFR,
                            nl: item.urlHomeNL,
                        },
                        isDefault: item.parDefaut,
                    };
                });
            }),
        );
    }

    /** Retourne le nom traduit d'une application sur base du ressourceType associé */
    public getApplicationName(ressourceType: string, language: Language): Observable<string> {
        const url = `/api/Application/${ressourceType}/${language}`;
        return this.http.get<ApiOneResponse<string>>(url).pipe(one());
    }
}
